/**
 * Anfragen: Tabelle mit Status-Tabs + Detail-Drawer (Stitch: maestro_anfragen)
 * + "Anfrage anlegen" als Drawer-Formular (POST /api/inquiries).
 * Statuswechsel via PATCH /api/inquiries/:id (falls Route fehlt: Hinweis im Drawer).
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { Inquiry } from "../ui/types";
import { INQUIRY_STATUS, inquirySourceMeta, formatDate, formatDateTime, timeAgo } from "../ui/format";
import {
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  LoadingRows,
  PageHeader,
  StatusPill,
} from "../ui/components";

const TABS: { key: string; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "new", label: "Neu" },
  { key: "quoted", label: "Angebot gesendet" },
  { key: "won", label: "Gewonnen" },
  { key: "lost", label: "Verloren" },
];

/** Kompakte Quellen-Labels für die Listen-Pill (Design 2b: „Formular"/„E-Mail" statt Langform). */
const SOURCE_SHORT: Record<string, string> = {
  manual: "Manuell",
  form: "Formular",
  chat: "KI-Chat",
  email: "E-Mail",
  phone: "Telefon",
};
const PAGE_SIZE = 8;

/** Controlled-Form-Zustand des "Anfrage anlegen"-Drawers (damit der KI-Import Felder befuellen kann). */
const EMPTY_FORM = { customerName: "", customerEmail: "", eventDate: "", guests: "", message: "" };
type CreateForm = typeof EMPTY_FORM;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Eckdaten-Zelle im Drawer (Design 2c): kleines Caps-Label über dem Wert. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">{label}</div>
      <div className="mt-1 text-[13px] font-medium text-on-surface dark:text-inverse-on-surface">{value}</div>
    </div>
  );
}

export default function InquiriesPage() {
  const { data, loading, error, reload } = useApi<Inquiry[]>("/api/inquiries");
  const { data: catalogItems } = useApi<{ id: string }[]>("/api/catalog/items");
  const [tab, setTab] = useState("all");
  // Toolbar-Filter (E2): Freitextsuche + Wunschdatum- + Quelle-Filter, danach clientseitige Pagination.
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<"all" | "upcoming" | "past">("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Inquiry | null>(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(params.get("neu") === "1");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingInquiry, setGeneratingInquiry] = useState<Inquiry | null>(null);
  const [genStep, setGenStep] = useState(0);
  const generateAbortRef = useRef<AbortController | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // Kalender verlinkt mit ?neu=1&datum=YYYY-MM-DD auf einen leeren Tag -> Wunschdatum vorbelegen.
  const [form, setForm] = useState<CreateForm>(() => {
    const datum = params.get("datum");
    return datum ? { ...EMPTY_FORM, eventDate: datum } : EMPTY_FORM;
  });
  const [kiText, setKiText] = useState("");
  const [kiBusy, setKiBusy] = useState(false);
  const [kiNote, setKiNote] = useState<string | null>(null);
  const navigate = useNavigate();

  // A-04: Der Sidebar-CTA "Neue Anfrage" navigiert zu /anfragen?neu=1. React Router aktualisiert
  // die Search-Params OHNE Remount, daher greift der useState-Initializer nicht erneut. Dieser
  // Effekt oeffnet den Anlege-Drawer reaktiv (und uebernimmt ein Wunschdatum aus ?datum=), auch
  // wenn man bereits auf /anfragen ist.
  useEffect(() => {
    if (params.get("neu") === "1") {
      setCreateOpen(true);
      const datum = params.get("datum");
      if (datum) setForm((f) => ({ ...f, eventDate: datum }));
    }
  }, [params]);

  // CX-Fix 2026-07-18: ?open=<inquiryId> öffnet den Detail-Drawer der KONKRETEN Anfrage.
  // Anfragen haben keine eigene Detailroute (bewusst Drawer-Muster) — dieser Deep-Link ist
  // das Ziel der Glocken-Meldungen, Dashboard-Todos und Kundenprofil-Einträge, die vorher
  // nur stumpf auf der Liste landeten.
  useEffect(() => {
    const openId = params.get("open");
    if (!openId || !data) return;
    const inq = data.find((i) => i.id === openId);
    if (inq) setSelected(inq);
  }, [params, data]);

  /** Detail-Drawer schließen + ?open= aufräumen (sonst öffnet der nächste Daten-Reload ihn erneut). */
  function closeDetail() {
    setSelected(null);
    if (params.get("open")) {
      params.delete("open");
      setParams(params, { replace: true });
    }
  }

  const inquiries = data ?? [];
  const filtered = useMemo(
    () => (tab === "all" ? inquiries : inquiries.filter((i) => i.status === tab)),
    [inquiries, tab],
  );
  const tabCounts = useMemo(() => {
    const m: Record<string, number> = { all: inquiries.length };
    for (const t of TABS) if (t.key !== "all") m[t.key] = inquiries.filter((i) => i.status === t.key).length;
    return m;
  }, [inquiries]);
  const recentCount = useMemo(() => {
    const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
    return inquiries.filter((i) => new Date(i.createdAt).getTime() >= cutoff).length;
  }, [inquiries]);
  const openCount = tabCounts.new ?? 0;

  // Welche Quellen kommen in den Daten vor -> nur diese im Filter anbieten.
  const availableSources = useMemo(() => {
    const set = new Set(inquiries.map((i) => i.source).filter(Boolean));
    return Array.from(set);
  }, [inquiries]);

  // Toolbar-Filter auf die (per Tab vorgefilterte) Liste anwenden.
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = Date.now();
    return filtered.filter((i) => {
      if (q && !`${i.customerName} ${i.customerEmail ?? ""} ${i.message ?? ""}`.toLowerCase().includes(q)) return false;
      if (sourceFilter !== "all" && i.source !== sourceFilter) return false;
      if (dateFilter !== "all") {
        const t = i.eventDate ? new Date(i.eventDate).getTime() : null;
        if (t == null) return false;
        if (dateFilter === "upcoming" && t < now) return false;
        if (dateFilter === "past" && t >= now) return false;
      }
      return true;
    });
  }, [filtered, query, sourceFilter, dateFilter]);

  const pageCount = Math.max(1, Math.ceil(searched.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const paged = searched.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  // Bei Wechsel von Tab/Filter/Suche auf Seite 1 zurück.
  useEffect(() => {
    setPage(0);
  }, [tab, query, sourceFilter, dateFilter]);

  function closeCreate() {
    setCreateOpen(false);
    setForm(EMPTY_FORM);
    setKiText("");
    setKiNote(null);
    setFormError(null);
    params.delete("neu");
    setParams(params, { replace: true });
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/inquiries", "POST", {
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        eventDate: form.eventDate ? new Date(form.eventDate).toISOString() : undefined,
        guests: form.guests ? Number(form.guests) : undefined,
        message: form.message || undefined,
      });
      closeCreate();
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** KI-Import: eingefügten E-Mail-/Freitext parsen und die Formularfelder vorbefüllen. */
  async function importFromText() {
    if (!kiText.trim()) return;
    setKiBusy(true);
    setKiNote(null);
    setFormError(null);
    try {
      const parsed = await apiMutate<Partial<CreateForm & { occasion: string }>>(
        "/api/inquiries/parse-text",
        "POST",
        { text: kiText },
      );
      const hasAny = parsed && Object.values(parsed).some((v) => v != null && v !== "");
      if (!hasAny) {
        setKiNote("Aus dem Text ließen sich keine Felder sicher erkennen – bitte manuell ausfüllen.");
        return;
      }
      // Nur erkannte Felder übernehmen; Bestehendes nicht mit Leerwerten überschreiben.
      setForm((f) => ({
        customerName: parsed.customerName || f.customerName,
        customerEmail: parsed.customerEmail || f.customerEmail,
        eventDate: parsed.eventDate || f.eventDate,
        guests: parsed.guests != null ? String(parsed.guests) : f.guests,
        // Anlass vorne an die Nachricht stellen, falls erkannt und noch nicht enthalten.
        message:
          parsed.message ||
          [parsed.occasion, f.message].filter(Boolean).join(parsed.occasion && f.message ? " – " : "") ||
          f.message,
      }));
      setKiNote("Felder übernommen – bitte prüfen und ggf. korrigieren.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setKiNote(/ai_unavailable|nicht konfiguriert|503/i.test(msg) ? "KI-Import ist nicht konfiguriert." : msg);
    } finally {
      setKiBusy(false);
    }
  }

  async function setStatus(inq: Inquiry, status: string) {
    setBusy(true);
    try {
      await apiMutate(`/api/inquiries/${inq.id}`, "PATCH", { status });
      closeDetail();
      // CX-Audit 2026-07-17: Nach „Gewonnen" nicht zurück in die Liste, sondern direkt zum
      // gewonnenen Vorgang — dort steht der nächste Schritt an (Abrechnung/Zahlung).
      if (status === "won" && inq.generatedEventId) {
        navigate(`/veranstaltungen/${inq.generatedEventId}`);
        return;
      }
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** ✨ Herzstueck: Anfrage -> KI-Angebotsentwurf -> direkt in den Builder.
   *  Die eigentliche Erzeugung ist EIN Request (keine echten Zwischen-Events vom Server) —
   *  die Schritt-Anzeige im Overlay wird deshalb zeitgesteuert simuliert; nur Schritt 1
   *  (Gästezahl) und Schritt 3 (Katalogumfang) zeigen echte, bereits bekannte Daten. */
  async function generateOffer(inq: Inquiry) {
    const controller = new AbortController();
    generateAbortRef.current = controller;
    setGenerating(true);
    setGeneratingInquiry(inq);
    setGenStep(0);
    setFormError(null);
    const t1 = setTimeout(() => setGenStep(1), 500);
    const t2 = setTimeout(() => setGenStep(2), 1400);
    try {
      const res = await apiMutate<{ eventId: string; itemCount: number; alreadyGenerated?: boolean }>(
        `/api/inquiries/${inq.id}/generate-offer`,
        "POST",
        {},
        controller.signal,
      );
      clearTimeout(t1);
      clearTimeout(t2);
      setGenStep(3);
      // Direkt in den Builder springen. M-38/M-40: alreadyGenerated liefert dasselbe Event -
      // auch ein Retry nach Browser-Abbruch landet im EINEN existierenden Entwurf statt in
      // einem Duplikat.
      navigate(`/veranstaltungen/${res.eventId}/angebot`);
    } catch (err) {
      clearTimeout(t1);
      clearTimeout(t2);
      setGenerating(false);
      setGeneratingInquiry(null);
      if (err instanceof DOMException && err.name === "AbortError") {
        // M-40: der Abbruch stoppt nur den Browser-Request - der Server kann die Erzeugung
        // bereits abgeschlossen haben. Liste neu laden: taucht generatedEventId auf, zeigt die
        // Anfrage den "Zum Angebot"-Button; ein erneutes Generieren liefert dasselbe Event.
        reload();
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      // M-39/M-38: Serverfehler in klare, handlungsleitende Texte uebersetzen.
      if (/generation_in_progress|laeuft bereits|läuft bereits/i.test(msg)) {
        setFormError("Für diese Anfrage läuft die Angebotserzeugung bereits (anderer Tab oder vorheriger Versuch). Bitte kurz warten und die Liste neu laden.");
        reload();
      } else if (/ai_failed|fehlgeschlagen.*KEIN Entwurf/i.test(msg)) {
        setFormError("Die KI-Analyse ist fehlgeschlagen – es wurde kein Entwurf angelegt. Bitte erneut versuchen.");
      } else if (/ai_not_configured/i.test(msg)) {
        setFormError("Kein KI-Zugang konfiguriert – der Angebotsentwurf braucht die KI-Analyse (siehe Einstellungen → System).");
      } else {
        setFormError(
          /empty_catalog|leer/i.test(msg)
            ? "Dein Katalog ist noch leer. Lege zuerst Positionen an oder importiere eine Speisekarte (Katalog → Import)."
            : msg,
        );
      }
    } finally {
      generateAbortRef.current = null;
    }
  }

  function cancelGenerate() {
    generateAbortRef.current?.abort();
  }

  /** Zum bereits erzeugten Angebot springen (statt ein zweites anzulegen).
   *  Ohne verknuepftes Event gibt es kein Ziel -> nichts tun (der Button wird dann gar nicht
   *  angezeigt; frueher landete man faelschlich in der Veranstaltungsliste). */
  function goToOffer(inq: Inquiry) {
    if (!inq.generatedEventId) return;
    navigate(`/veranstaltungen/${inq.generatedEventId}/angebot`);
  }

  return (
    <>
      <PageHeader
        title="Anfragen"
        subtitle={inquiries.length > 0 ? `${recentCount} Anfragen in den letzten 90 Tagen · ${openCount} offen` : "Verwalten und priorisieren Sie alle eingehenden Kundenanfragen."}
        actions={
          <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
            <Icon name="add" className="text-[18px]" /> Anfrage anlegen
          </button>
        }
      />

      {/* Historischer Hinweis (Phase 5): die einmalig migrierten Alt-Vorgaenge mit Status
          "Anfrage" liegen als Events unter Veranstaltungen - nicht hier. Persistente,
          unaufdringliche Infobox oberhalb der Liste, damit sie nach der ersten neuen Anfrage
          nicht verschwindet (zusaetzlich zum Empty-State-Hinweis fuer kuenftige Anfragen). */}
      <div className="mb-4 flex items-start gap-2 rounded-[12px] border border-outline-variant/40 bg-surface-container-low px-4 py-3 text-body-sm text-on-surface-variant dark:border-outline/30 dark:bg-surface-variant/10">
        <Icon name="info" className="mt-0.5 text-[18px] text-on-surface-variant/70" />
        <p>
          Historisch importierte Vorgänge mit Status „Anfrage“ befinden sich unter{" "}
          <Link to="/veranstaltungen" className="font-medium text-primary underline underline-offset-2">
            Veranstaltungen
          </Link>
          .
        </p>
      </div>

      <div className="card overflow-hidden">
        {/* Status-Tabs */}
        <div className="tab-scroll flex border-b border-outline-variant/30 px-2 dark:border-outline/30">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap border-b-2 px-6 py-4 font-label-md text-label-md transition-colors ${
                tab === t.key
                  ? "border-primary bg-primary-container/5 text-primary dark:bg-primary-container/10"
                  : "border-transparent text-secondary hover:bg-surface-container-high hover:text-primary dark:text-secondary-fixed-dim dark:hover:bg-surface-variant"
              }`}
            >
              {t.label} <span className="text-on-surface-variant/60">{tabCounts[t.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {error && <ErrorNote message={error} onRetry={reload} />}
        {loading ? (
          <LoadingRows />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="inbox"
            title="Keine Anfragen in dieser Ansicht"
            hint="Sobald über Ihr Anfrageformular oder per E-Mail eine Anfrage eingeht, erscheint sie automatisch hier."
            cta={{ label: "Anfrage manuell anlegen", icon: "add", onClick: () => setCreateOpen(true) }}
          />
        ) : (
          <>
            {/* Toolbar (E2): Suche + Wunschdatum-/Quelle-Filter + Sortierhinweis */}
            <div className="flex flex-wrap items-center gap-2.5 border-b border-outline-variant/30 px-[18px] py-3 dark:border-outline/30">
              <div className="flex min-w-[220px] items-center gap-2 rounded-[9px] border border-outline-variant bg-surface-container-low px-3 py-1.5 dark:bg-surface-variant/10">
                <Icon name="search" className="text-[16px] text-on-surface-variant/70" />
                <input
                  className="w-full bg-transparent text-[12px] text-on-surface outline-none placeholder:text-on-surface-variant/60 dark:text-inverse-on-surface"
                  placeholder="In Anfragen suchen …"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <label className="relative">
                <select
                  className="appearance-none rounded-[9px] border border-outline-variant bg-surface-container-lowest py-1.5 pl-3 pr-8 text-[12px] font-medium text-on-surface-variant outline-none dark:bg-inverse-surface dark:text-inverse-on-surface"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
                >
                  <option value="all">Wunschdatum: Alle</option>
                  <option value="upcoming">Wunschdatum: Kommende</option>
                  <option value="past">Wunschdatum: Vergangene</option>
                </select>
                <Icon name="expand_more" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[16px] text-on-surface-variant/70" />
              </label>
              <label className="relative">
                <select
                  className="appearance-none rounded-[9px] border border-outline-variant bg-surface-container-lowest py-1.5 pl-3 pr-8 text-[12px] font-medium text-on-surface-variant outline-none dark:bg-inverse-surface dark:text-inverse-on-surface"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <option value="all">Quelle: Alle</option>
                  {availableSources.map((s) => (
                    <option key={s} value={s}>
                      Quelle: {SOURCE_SHORT[s] ?? inquirySourceMeta(s).label}
                    </option>
                  ))}
                </select>
                <Icon name="expand_more" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[16px] text-on-surface-variant/70" />
              </label>
              <span className="ml-auto font-body-sm text-[11.5px] text-on-surface-variant/70">Sortiert nach: Eingegangen ↓</span>
            </div>

            {paged.length === 0 ? (
              <EmptyState icon="search_off" title="Keine Treffer" hint="Passen Sie Suche oder Filter an." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] border-collapse text-left">
                  <thead>
                    <tr>
                      <th className="table-header">Kunde</th>
                      <th className="table-header text-right">Gäste</th>
                      <th className="table-header">Wunschdatum</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Nachricht</th>
                      <th className="table-header">Eingegangen</th>
                      <th className="table-header">Quelle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/20 dark:divide-outline/20">
                    {paged.map((i) => {
                      const isNew = i.status === "new";
                      return (
                        <tr
                          key={i.id}
                          className="group cursor-pointer transition-colors hover:bg-surface-container-high/30 dark:hover:bg-surface-variant/30"
                          onClick={() => setSelected(i)}
                        >
                          <td className="table-cell align-middle">
                            <div className="flex items-center gap-3">
                              <span
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                  isNew
                                    ? "bg-primary/10 text-primary"
                                    : "bg-surface-container-high text-secondary dark:bg-surface-variant/40 dark:text-secondary-fixed-dim"
                                }`}
                              >
                                {initials(i.customerName)}
                              </span>
                              <div className="min-w-0">
                                <p className="truncate font-medium text-on-surface dark:text-inverse-on-surface">
                                  {i.customerName}
                                </p>
                                {i.customerEmail && (
                                  <p className="truncate font-body-sm text-xs text-on-surface-variant/80">
                                    {i.customerEmail}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="table-cell whitespace-nowrap text-right align-middle font-medium text-on-surface dark:text-inverse-on-surface">
                            {i.guests ?? "–"}
                          </td>
                          <td className="table-cell whitespace-nowrap align-middle text-on-surface dark:text-inverse-on-surface">
                            {formatDate(i.eventDate)}
                          </td>
                          <td className="table-cell align-middle">
                            <StatusPill meta={INQUIRY_STATUS[i.status]} />
                          </td>
                          <td className="table-cell max-w-[260px] align-middle">
                            {i.message ? (
                              <p className="max-w-[260px] truncate font-body-sm text-on-surface-variant">
                                „{i.message}"
                              </p>
                            ) : (
                              <span className="text-on-surface-variant/50">–</span>
                            )}
                          </td>
                          <td className="table-cell whitespace-nowrap align-middle text-on-surface-variant">
                            {timeAgo(i.createdAt)}
                          </td>
                          <td className="table-cell align-middle">
                            <span className="whitespace-nowrap rounded-full bg-[#F0EAE0] px-2 py-0.5 text-[11px] font-medium text-[#57503F] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                              {SOURCE_SHORT[i.source] ?? inquirySourceMeta(i.source).label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination (E2) */}
            <div className="flex items-center justify-between border-t border-outline-variant/30 bg-surface-bright px-[18px] py-3 dark:border-outline/30 dark:bg-white/[0.02]">
              <span className="font-body-sm text-[11.5px] text-on-surface-variant/70">
                {searched.length === 0
                  ? "Keine Anfragen"
                  : `${safePage * PAGE_SIZE + 1}–${safePage * PAGE_SIZE + paged.length} von ${searched.length} Anfragen`}
              </span>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  className="rounded-lg border border-outline-variant px-3 py-1.5 text-[11.5px] font-medium text-on-surface-variant transition-colors enabled:hover:bg-surface-container-high disabled:text-on-surface-variant/40 dark:hover:bg-surface-variant"
                  disabled={safePage <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Zurück
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-outline-variant px-3 py-1.5 text-[11.5px] font-medium text-on-surface-variant transition-colors enabled:hover:bg-surface-container-high disabled:text-on-surface-variant/40 dark:hover:bg-surface-variant"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                >
                  Weiter
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail-Drawer (Design 2c) */}
      <Drawer
        open={selected != null}
        onClose={() => {
          closeDetail();
          setStatusMenuOpen(false);
        }}
        width="540px"
        title={selected?.customerName ?? "Anfrage"}
        subtitle={
          selected
            ? `Anfrage · eingegangen ${formatDateTime(selected.createdAt)} · via ${
                SOURCE_SHORT[selected.source] ?? inquirySourceMeta(selected.source).label
              }`
            : undefined
        }
        headerAccessory={selected ? <StatusPill meta={INQUIRY_STATUS[selected.status]} /> : undefined}
        footer={
          selected &&
          (() => {
            const inq = selected;
            const hasEvent = !!inq.generatedEventId;
            const canGenerate = inq.status === "new" || inq.status === "quoted";
            return (
              <div className="flex w-full flex-col gap-2.5">
                <div className="flex items-center gap-2.5">
                  {hasEvent ? (
                    <button
                      type="button"
                      className="btn-primary flex-1 justify-center py-2.5"
                      disabled={busy}
                      onClick={() => goToOffer(inq)}
                    >
                      <Icon name="arrow_forward" className="text-[18px]" /> Zum Angebot
                    </button>
                  ) : canGenerate ? (
                    <button
                      type="button"
                      className="btn-primary flex-1 justify-center py-2.5"
                      disabled={busy || generating}
                      onClick={() => void generateOffer(inq)}
                    >
                      <Icon name="auto_awesome" className="text-[18px]" /> KI-Angebot erzeugen
                    </button>
                  ) : (
                    <span className="flex-1" />
                  )}

                  {/* Status ändern (E6) — konsolidiert die manuellen Statuswechsel in ein Menü. */}
                  <div className="relative shrink-0">
                    <button
                      type="button"
                      className="btn-secondary !py-2.5"
                      disabled={busy}
                      onClick={() => setStatusMenuOpen((v) => !v)}
                    >
                      Status ändern
                      <Icon name="expand_more" className="text-[18px]" />
                    </button>
                    {statusMenuOpen && (
                      <div className="absolute bottom-full right-0 z-10 mb-2 w-52 overflow-hidden rounded-xl border border-outline-variant/50 bg-surface-container-lowest py-1 shadow-xl dark:border-outline/40 dark:bg-night-card">
                        {/* M-42: nur serverseitig erlaubte Uebergaenge anbieten - 'quoted' vergibt
                            generate-offer, 'won' braucht ein erzeugtes Angebot. */}
                        {Object.entries(INQUIRY_STATUS)
                          .filter(([key]) => {
                            const allowed: Record<string, string[]> = { new: ["lost"], quoted: ["won", "lost"], lost: ["new"], won: [] };
                            if (!(allowed[inq.status] ?? []).includes(key)) return false;
                            if (key === "won" && !inq.generatedEventId) return false;
                            return true;
                          })
                          .map(([key, meta]) => (
                            <button
                              key={key}
                              type="button"
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-on-surface transition-colors hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-surface-variant"
                              onClick={() => {
                                setStatusMenuOpen(false);
                                void setStatus(inq, key);
                              }}
                            >
                              <StatusPill meta={meta} />
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
                {canGenerate && !hasEvent && (
                  <p className="font-body-sm text-[11px] leading-relaxed text-on-surface-variant/70">
                    Die KI stellt aus Anfrage, Katalog &amp; Paketen einen Entwurf samt Anschreiben zusammen — gesendet
                    wird erst nach deiner Freigabe.
                  </p>
                )}
              </div>
            );
          })()
        }
      >
        {selected && (
          <div className="space-y-[18px]">
            {formError && <ErrorNote message={formError} />}

            {/* Kontakt-Karte */}
            <div className="flex items-center gap-3 rounded-xl border border-surface-variant bg-surface-container-lowest p-3.5 dark:border-white/10 dark:bg-night-card">
              <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-primary/14 text-[13px] font-bold text-primary">
                {initials(selected.customerName)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-on-surface dark:text-inverse-on-surface">
                  {selected.customerName}
                </p>
                {selected.customerEmail && (
                  <p className="mt-0.5 truncate font-body-sm text-[12px] text-on-surface-variant">
                    {/* CX-Audit 2026-07-17: E-Mail als mailto (wie auf Event-/Kunden-Detail),
                        statt reiner Text — direkter Rückkanal aus der Anfrage heraus. */}
                    <a href={`mailto:${selected.customerEmail}`} className="hover:text-primary hover:underline">
                      {selected.customerEmail}
                    </a>
                  </p>
                )}
              </div>
            </div>

            {/* Eckdaten (E4) — 3-Spalten-Grid aus den vorhandenen Feldern */}
            <div className="grid grid-cols-3 gap-x-4 gap-y-3.5 px-0.5">
              <Fact label="Wunschdatum" value={formatDate(selected.eventDate)} />
              <Fact label="Gäste" value={selected.guests ? `${selected.guests} Personen` : "–"} />
              <Fact label="Quelle" value={SOURCE_SHORT[selected.source] ?? inquirySourceMeta(selected.source).label} />
              <Fact label="Eingegangen" value={formatDate(selected.createdAt)} />
            </div>

            {/* Nachricht */}
            {selected.message && (
              <div className="rounded-xl border border-surface-variant bg-surface-container-low p-3.5 dark:border-white/10 dark:bg-surface-variant/10">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">
                  Nachricht
                </p>
                <p className="whitespace-pre-wrap font-body-sm text-[12.5px] leading-relaxed text-on-surface dark:text-inverse-on-surface">
                  {selected.message}
                </p>
              </div>
            )}

            {/* Aktivität (E5) — aus dem echten Lebenszyklus abgeleitet */}
            {(() => {
              const src = SOURCE_SHORT[selected.source] ?? inquirySourceMeta(selected.source).label;
              const activity: { label: string; time: string | null }[] = [
                { label: `Anfrage über ${src} eingegangen`, time: formatDateTime(selected.createdAt) },
              ];
              if (selected.generatedEventId) activity.push({ label: "KI-Angebot erstellt", time: null });
              if (selected.status === "quoted") activity.push({ label: "Angebot als gesendet markiert", time: null });
              if (selected.status === "won") activity.push({ label: "Als gewonnen markiert", time: null });
              if (selected.status === "lost") activity.push({ label: "Als verloren markiert", time: null });
              return (
                <div>
                  <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">
                    Aktivität
                  </p>
                  <div className="flex flex-col">
                    {activity.map((ev, i) => {
                      const first = i === 0;
                      const last = i === activity.length - 1;
                      return (
                        <div key={i} className="flex gap-3">
                          <div className="flex shrink-0 flex-col items-center">
                            <span
                              className={`mt-1 h-[9px] w-[9px] rounded-full ${
                                first ? "bg-primary" : "bg-outline-variant"
                              }`}
                            />
                            {!last && <span className="w-px flex-1 bg-outline-variant/60" />}
                          </div>
                          <div className={last ? "" : "pb-3.5"}>
                            {/* CX-Audit 2026-07-17: „KI-Angebot erstellt" verlinkt zum Builder
                                (vorher toter Text trotz vorhandener generatedEventId). */}
                            {ev.label === "KI-Angebot erstellt" && selected.generatedEventId ? (
                              <button
                                type="button"
                                onClick={() => goToOffer(selected)}
                                className="font-body-sm text-[12.5px] font-medium text-primary hover:underline"
                              >
                                {ev.label} — öffnen
                              </button>
                            ) : (
                              <p className="font-body-sm text-[12.5px] font-medium text-on-surface dark:text-inverse-on-surface">
                                {ev.label}
                              </p>
                            )}
                            {ev.time && (
                              <p className="mt-0.5 font-body-sm text-[11px] text-on-surface-variant/70">{ev.time}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </Drawer>

      {/* Anlegen-Drawer */}
      <Drawer open={createOpen} onClose={closeCreate} title="Anfrage anlegen">
        {/* ✨ KI-Import: E-Mail/Freitext einfügen -> Felder werden vorbefüllt. */}
        <div className="mb-6 rounded-xl border border-primary/30 bg-primary-container/20 p-4 dark:bg-primary-container/10">
          <div className="mb-1 flex items-center gap-2">
            <Icon name="auto_awesome" className="text-[18px] text-primary" />
            <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              KI-Import
            </span>
          </div>
          <p className="mb-3 font-body-sm text-xs text-secondary dark:text-secondary-fixed-dim">
            E-Mail oder Freitext der Anfrage einfügen – die KI füllt die Felder unten aus. Bitte danach prüfen.
          </p>
          <textarea
            rows={4}
            className="input"
            placeholder="Text der Anfrage hier einfügen (z. B. eine weitergeleitete E-Mail) …"
            value={kiText}
            onChange={(e) => setKiText(e.target.value)}
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="font-body-sm text-xs text-secondary dark:text-secondary-fixed-dim">{kiNote ?? ""}</span>
            <button
              type="button"
              className="btn-secondary shrink-0"
              disabled={kiBusy || !kiText.trim()}
              onClick={() => void importFromText()}
            >
              <Icon name="auto_awesome" className="text-[16px]" /> {kiBusy ? "Liest …" : "Aus Text übernehmen"}
            </button>
          </div>
        </div>

        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Kundenname *
            </span>
            <input
              required
              className="input"
              placeholder="z. B. Meier & Weber GmbH"
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              E-Mail *
            </span>
            <input
              required
              type="email"
              className="input"
              placeholder="kontakt@beispiel.de"
              value={form.customerEmail}
              onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Wunschdatum
              </span>
              <input
                type="date"
                className="input"
                value={form.eventDate}
                onChange={(e) => setForm((f) => ({ ...f, eventDate: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Gäste
              </span>
              <input
                type="number"
                min="1"
                className="input"
                placeholder="80"
                value={form.guests}
                onChange={(e) => setForm((f) => ({ ...f, guests: e.target.value }))}
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Nachricht
            </span>
            <textarea
              rows={5}
              className="input"
              placeholder="Worum geht es? (Anlass, Wünsche, Budget …)"
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={closeCreate}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Speichert …" : "Anfrage anlegen"}
            </button>
          </div>
        </form>
      </Drawer>

      {/* ✨ KI-Moment: Modal waehrend der Entwurf entsteht (Design-Entwurf: Frame 7a „Ihr Angebot entsteht").
          z-[70] MUSS ueber dem Drawer (z-[60]) liegen — sonst milcht dessen Backdrop-Blur das Popup ein. */}
      {generating && (() => {
        const steps: { label: string; detail: string }[] = [
          { label: "Anfrage analysiert", detail: generatingInquiry?.guests ? `${generatingInquiry.guests} Gäste` : "" },
          { label: "Preis-Anker gewählt", detail: "aus Katalog & Paketen" },
          {
            label: "Positionen werden kalkuliert",
            detail: catalogItems ? `Katalog · ${catalogItems.length} Positionen` : "",
          },
          { label: "Anschreiben formulieren", detail: "" },
        ];
        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-on-surface/30 p-[30px] backdrop-blur-sm">
            <div className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-surface-container-lowest shadow-xl dark:bg-surface-dim">
              <div className="flex flex-col items-center gap-[13px] px-[30px] pb-1 pt-7 text-center">
                <div className="grid h-[54px] w-[54px] place-items-center rounded-2xl bg-primary-container text-primary dark:bg-primary/15">
                  <Icon name="auto_awesome" className="text-2xl" />
                </div>
                <h3 className="font-headline-sm text-[19px] font-bold tracking-tight text-on-surface dark:text-inverse-on-surface">
                  Ihr Angebot entsteht
                </h3>
                <p className="max-w-[400px] font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
                  Die KI liest die Anfrage und stellt aus Katalog &amp; Paketen einen Entwurf zusammen.
                </p>
              </div>

              <ul className="px-[30px] pt-[14px]">
                {steps.map((step, i) => {
                  const done = i < genStep;
                  const active = i === genStep;
                  return (
                    <li
                      key={step.label}
                      className={`grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-[11px] py-2.5 ${
                        i < steps.length - 1 ? "border-b border-outline-variant/60 dark:border-outline/40" : ""
                      }`}
                    >
                      {done ? (
                        <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-[#E4F1E8] text-[#217A50] dark:bg-[#2E9963]/20 dark:text-[#7bc49a]">
                          <Icon name="check" className="text-[13px]" />
                        </span>
                      ) : active ? (
                        <span className="grid h-[22px] w-[22px] place-items-center rounded-full border-2 border-primary bg-primary-container/50 text-primary">
                          <Icon name="progress_activity" className="animate-spin text-[13px]" />
                        </span>
                      ) : (
                        <span className="h-[22px] w-[22px] rounded-full border-[1.5px] border-dashed border-outline-variant" />
                      )}
                      <span
                        className={`text-[12.5px] ${
                          active
                            ? "font-semibold text-primary"
                            : done
                              ? "font-semibold text-on-surface dark:text-inverse-on-surface"
                              : "font-medium text-secondary/70 dark:text-secondary-fixed-dim/70"
                        }`}
                      >
                        {step.label}
                      </span>
                      <span className="text-right text-[11px] text-on-surface-variant/70">{step.detail}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="mx-[30px] mt-3 h-[6px] overflow-hidden rounded-full bg-surface-container-high dark:bg-surface-variant/40">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${((genStep + 1) / steps.length) * 100}%` }}
                />
              </div>

              <div className="flex items-center justify-between gap-3 px-[30px] pb-[22px] pt-[14px]">
                <p className="text-[11px] text-on-surface-variant/70">
                  Gesendet wird erst nach Ihrer Freigabe — der Entwurf öffnet sich im Builder.
                </p>
                <button type="button" className="btn-secondary shrink-0" onClick={cancelGenerate}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
